
import { useState, useCallback } from 'react';
import { Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import { PrayerStatus, PrayerType } from '@/types';

/** Partner response workflow filter (API: `video_response_status`) */
export type VideoResponseFilter = 'all' | 'awaiting' | 'responded';

export interface FilterState {
  status: PrayerStatus | 'all';
  /** Filter by partner response workflow; independent of status */
  videoResponseStatus: VideoResponseFilter;
  type: PrayerType | 'all';
  category: string;
  dateRange: 'all' | 'today' | 'week' | 'month' | 'custom';
  search: string;
  sortOrder: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
}

export interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  isLoading?: boolean;
  /** When false, hide the partner response workflow filter. Defaults to true. */
  showResponseFilter?: boolean;
}

const statusOptions: { value: PrayerStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: PrayerStatus.PENDING, label: 'Pending' },
  { value: PrayerStatus.QUEUED, label: 'Queued' },
  { value: PrayerStatus.DISPLAYED, label: 'Displayed' },
  { value: PrayerStatus.ARCHIVED, label: 'Dismissed' },
];

const videoResponseFilterOptions: { value: VideoResponseFilter; label: string }[] = [
  { value: 'all', label: 'Any response state' },
  { value: 'awaiting', label: 'Awaiting partner response' },
  { value: 'responded', label: 'Partner responded' },
];

const typeOptions: { value: PrayerType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: PrayerType.PRAYER_REQUEST, label: 'Prayer Request' },
  { value: PrayerType.PRAISE_REPORT, label: 'Answered Prayer' },
];

const categoryOptions = [
  { value: 'all', label: 'All Categories' },
  { value: 'healing', label: 'Healing' },
  { value: 'finances', label: 'Finances' },
  { value: 'family', label: 'Family' },
  { value: 'spiritual', label: 'Spiritual' },
  { value: 'other', label: 'Other' },
];

const dateRangeOptions = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

export function FilterBar({
  filters,
  onFilterChange,
  isLoading = false,
  showResponseFilter = true,
}: FilterBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChange = useCallback(
    (key: keyof FilterState, value: string) => {
      const nextFilters: FilterState = {
        ...filters,
        [key]: value,
      };
      onFilterChange(nextFilters);
    },
    [filters, onFilterChange]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({
        ...filters,
        search: e.target.value,
      });
    },
    [filters, onFilterChange]
  );

  const clearFilters = useCallback(() => {
    onFilterChange({
      status: 'all',
      videoResponseStatus: 'all',
      type: 'all',
      category: 'all',
      dateRange: 'all',
      search: '',
      sortOrder: 'asc',
      dateFrom: undefined,
      dateTo: undefined,
    });
  }, [onFilterChange]);

  const sortOrderOptions: { value: 'asc' | 'desc'; label: string }[] = [
    { value: 'asc', label: 'Oldest first' },
    { value: 'desc', label: 'Newest first' },
  ];

  const hasActiveFilters =
    filters.status !== 'all' ||
    (showResponseFilter && filters.videoResponseStatus !== 'all') ||
    filters.type !== 'all' ||
    filters.category !== 'all' ||
    filters.dateRange !== 'all' ||
    filters.search !== '';

  return (
    <div className="overflow-x-hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      {/* Search and toggle row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Input
            type="search"
            placeholder="Search submissions..."
            value={filters.search}
            onChange={handleSearchChange}
            leftIcon={
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            }
          />
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Clear filters
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              'border border-slate-200 dark:border-slate-600',
              'hover:bg-slate-50 dark:hover:bg-slate-700',
              isExpanded && 'bg-slate-100 dark:bg-slate-700'
            )}
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filters
            {hasActiveFilters && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-xs text-white">
                {
                  [
                    filters.status !== 'all',
                    showResponseFilter && filters.videoResponseStatus !== 'all',
                    filters.type !== 'all',
                    filters.category !== 'all',
                    filters.dateRange !== 'all',
                  ].filter(Boolean).length
                }
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Expanded filters — animated to prevent layout jump */}
      <div
        aria-hidden={!isExpanded}
        className="transition-[max-height,opacity] duration-200 ease-out"
        style={{
          maxHeight: isExpanded ? 520 : 0,
          opacity: isExpanded ? 1 : 0,
          overflow: 'hidden',
        }}
      >
        <div className="mt-4 grid gap-4 border-t border-slate-200 pt-4 dark:border-slate-700 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {/* Status filter */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleChange('status', e.target.value)}
              disabled={isLoading}
              className={cn(
                'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                'disabled:opacity-50'
              )}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Partner video response filter (orthogonal to status) */}
          {showResponseFilter && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Response
              </label>
              <select
                value={filters.videoResponseStatus}
                onChange={(e) =>
                  handleChange('videoResponseStatus', e.target.value as VideoResponseFilter)
                }
                disabled={isLoading}
                className={cn(
                  'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                  'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                  'disabled:opacity-50'
                )}
              >
                {videoResponseFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Type filter */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Type
            </label>
            <select
              value={
                filters.type === PrayerType.PRAYER
                  ? PrayerType.PRAYER_REQUEST
                  : filters.type === PrayerType.PRAISE
                    ? PrayerType.PRAISE_REPORT
                    : filters.type
              }
              onChange={(e) => handleChange('type', e.target.value)}
              disabled={isLoading}
              className={cn(
                'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                'disabled:opacity-50'
              )}
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Category filter */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleChange('category', e.target.value)}
              disabled={isLoading}
              className={cn(
                'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                'disabled:opacity-50'
              )}
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date range filter */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Date Range
            </label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleChange('dateRange', e.target.value)}
              disabled={isLoading}
              className={cn(
                'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                'disabled:opacity-50'
              )}
            >
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort order */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Sort
            </label>
            <select
              value={filters.sortOrder}
              onChange={(e) => handleChange('sortOrder', e.target.value as 'asc' | 'desc')}
              disabled={isLoading}
              className={cn(
                'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                'disabled:opacity-50'
              )}
            >
              {sortOrderOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom date range inputs */}
          {filters.dateRange === 'custom' && (
            <>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleChange('dateFrom', e.target.value)}
                  disabled={isLoading}
                  className={cn(
                    'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                    'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                    'disabled:opacity-50'
                  )}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleChange('dateTo', e.target.value)}
                  disabled={isLoading}
                  className={cn(
                    'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                    'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                    'disabled:opacity-50'
                  )}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
